/**
 * Cross-Tab Authentication Sync
 *
 * Enables communication between browser tabs for auth events.
 * Uses BroadcastChannel API with localStorage fallback for older browsers.
 */

import { useEffect } from 'react';

const CHANNEL_NAME = 'growe_auth';
const STORAGE_KEY = 'growe_auth_event';

type AuthEventType = 'login' | 'logout';

interface AuthEvent {
  type: AuthEventType;
  timestamp: number;
}

/**
 * Check if BroadcastChannel is supported
 */
function isBroadcastChannelSupported(): boolean {
  return typeof BroadcastChannel !== 'undefined';
}

/**
 * Broadcast auth event to other tabs
 * Uses BroadcastChannel if available, falls back to localStorage
 */
export function broadcastAuthEvent(type: AuthEventType): void {
  const event: AuthEvent = {
    type,
    timestamp: Date.now(),
  };

  // Method 1: BroadcastChannel (modern browsers)
  if (isBroadcastChannelSupported()) {
    try {
      const channel = new BroadcastChannel(CHANNEL_NAME);
      channel.postMessage(event);
      channel.close();
    } catch (error) {
      console.error('BroadcastChannel error:', error);
    }
  }

  // Method 2: localStorage (fallback for older browsers + additional sync)
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(event));
    // Clear after a second to prevent stale events
    setTimeout(() => {
      const current = localStorage.getItem(STORAGE_KEY);
      if (current) {
        const parsed = JSON.parse(current) as AuthEvent;
        if (parsed.timestamp === event.timestamp) {
          localStorage.removeItem(STORAGE_KEY);
        }
      }
    }, 1000);
  } catch (error) {
    console.error('localStorage error:', error);
  }
}

/**
 * Convenience function for broadcasting login event
 */
export function broadcastLogin(): void {
  broadcastAuthEvent('login');
}

/**
 * Convenience function for broadcasting logout event
 */
export function broadcastLogout(): void {
  broadcastAuthEvent('logout');
}

/**
 * React hook to listen for auth events from other tabs
 * Also sends acknowledgement back when receiving login events
 *
 * @param onLogin - Callback when login event is received
 * @param onLogout - Callback when logout event is received
 */
export function useAuthListener(
  onLogin?: () => void,
  onLogout?: () => void
): void {
  useEffect(() => {
    let channel: BroadcastChannel | null = null;

    // Handler for BroadcastChannel messages
    const handleChannelMessage = (event: MessageEvent<AuthEvent>) => {
      if (event.data?.type === 'login') {
        // Send acknowledgement back
        sendAcknowledgement();

        if (onLogin) {
          onLogin();
        }
      } else if (event.data?.type === 'logout' && onLogout) {
        onLogout();
      }
    };

    // Handler for localStorage events (fallback + cross-window)
    const handleStorageEvent = (event: StorageEvent) => {
      if (event.key === STORAGE_KEY && event.newValue) {
        try {
          const authEvent = JSON.parse(event.newValue) as AuthEvent;
          if (authEvent.type === 'login') {
            // Send acknowledgement back
            sendAcknowledgement();

            if (onLogin) {
              onLogin();
            }
          } else if (authEvent.type === 'logout' && onLogout) {
            onLogout();
          }
        } catch (error) {
          console.error('Failed to parse auth event:', error);
        }
      }
    };

    // Setup BroadcastChannel listener
    if (isBroadcastChannelSupported()) {
      try {
        channel = new BroadcastChannel(CHANNEL_NAME);
        channel.addEventListener('message', handleChannelMessage);
      } catch (error) {
        console.error('Failed to create BroadcastChannel:', error);
      }
    }

    // Setup localStorage listener (always, as fallback + for cross-window)
    window.addEventListener('storage', handleStorageEvent);

    // Cleanup
    return () => {
      if (channel) {
        channel.removeEventListener('message', handleChannelMessage);
        channel.close();
      }
      window.removeEventListener('storage', handleStorageEvent);
    };
  }, [onLogin, onLogout]);
}

/**
 * Send acknowledgement that this tab received the login event
 */
function sendAcknowledgement(): void {
  const ackKey = 'growe_auth_ack';

  if (isBroadcastChannelSupported()) {
    try {
      const channel = new BroadcastChannel(CHANNEL_NAME);
      channel.postMessage({ type: 'ack', timestamp: Date.now() });
      channel.close();
    } catch (error) {
      console.error('BroadcastChannel ack error:', error);
    }
  }

  // Also use localStorage for fallback
  try {
    localStorage.setItem(ackKey, JSON.stringify({ timestamp: Date.now() }));
    setTimeout(() => localStorage.removeItem(ackKey), 100);
  } catch (error) {
    console.error('localStorage ack error:', error);
  }
}

/**
 * Wait for acknowledgement from other tabs
 * Returns true if other tabs are listening, false if not
 *
 * @param timeoutMs - How long to wait for acknowledgement (default 500ms)
 */
export function waitForAcknowledgement(timeoutMs: number = 500): Promise<boolean> {
  return new Promise((resolve) => {
    let acknowledged = false;
    let channel: BroadcastChannel | null = null;
    const ackKey = 'growe_auth_ack';

    const cleanup = () => {
      if (channel) {
        channel.removeEventListener('message', handleChannelAck);
        channel.close();
      }
      window.removeEventListener('storage', handleStorageAck);
    };

    const handleChannelAck = (event: MessageEvent) => {
      if (event.data?.type === 'ack') {
        acknowledged = true;
        cleanup();
        resolve(true);
      }
    };

    const handleStorageAck = (event: StorageEvent) => {
      if (event.key === ackKey && event.newValue) {
        acknowledged = true;
        cleanup();
        resolve(true);
      }
    };

    // Setup listeners
    if (isBroadcastChannelSupported()) {
      try {
        channel = new BroadcastChannel(CHANNEL_NAME);
        channel.addEventListener('message', handleChannelAck);
      } catch (error) {
        console.error('Failed to create BroadcastChannel for ack:', error);
      }
    }

    window.addEventListener('storage', handleStorageAck);

    // Timeout - no acknowledgement received
    setTimeout(() => {
      if (!acknowledged) {
        cleanup();
        resolve(false);
      }
    }, timeoutMs);
  });
}
